"use client";

import {
	createFileRoute,
	Outlet,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import Cookies from "js-cookie";
import { Suspense, useCallback, useEffect, useRef } from "react";
import { AppSidebar } from "@/components/layout/app-sidebar";
import SkipToMain from "@/components/skip-to-main";
import { FullPageSpinner } from "@/components/ui/full-page-spinner";
import { SidebarProvider } from "@/components/ui/sidebar";
import { SearchProvider } from "@/context/search-context";
import {
	type OrganizationsResponse,
	useOrganizationsQuery,
} from "@/hooks/queries/use-organizations-query";
import {
	createAdminOrgSelection,
	readAdminOrgSelection,
	resolveAdminFallbackTenantId,
	writeAdminOrgSelection,
} from "@/lib/admin-org-session";
import {
	RedirectToSignIn,
	SignedIn,
	useAuth,
	useOrganization,
	useOrganizationList,
	useUser,
} from "@/lib/auth-ui";
import { isSuperAdminUser } from "@/lib/super-admin";
import { cn } from "@/lib/utils";

const ROUTE_LOG_STORAGE_KEY = "lynx-route-logs";
const ADMIN_NAV_FLAG_STORAGE_KEY = "lynx-admin-navigation";
const MAX_ROUTE_LOG_ENTRIES = 20;
const RECENT_ADMIN_NAVIGATION_WINDOW_MS = 5000;
const ROUTING_DEBUG_ENABLED = import.meta.env.DEV;
const EMPTY_ORGANIZATIONS: OrganizationsResponse["organizations"] = [];

type RouteLogEntry = {
	timestamp: string;
	message: string;
	data?: unknown;
};

type AdminNavigationFlag = {
	timestamp: number;
	targetOrgId: string;
	isAdmin: boolean;
};

const appendRouteLog = (entry: RouteLogEntry) => {
	if (!ROUTING_DEBUG_ENABLED) {
		return;
	}

	try {
		const existing = localStorage.getItem(ROUTE_LOG_STORAGE_KEY);
		const logs: RouteLogEntry[] = existing ? JSON.parse(existing) : [];
		logs.push(entry);
		while (logs.length > MAX_ROUTE_LOG_ENTRIES) {
			logs.shift();
		}
		localStorage.setItem(ROUTE_LOG_STORAGE_KEY, JSON.stringify(logs));
	} catch (error: unknown) {
		console.error("Failed to append route log", error);
	}
};

const readAdminNavigationFlag = (): AdminNavigationFlag | null => {
	try {
		const rawValue = sessionStorage.getItem(ADMIN_NAV_FLAG_STORAGE_KEY);
		return rawValue ? (JSON.parse(rawValue) as AdminNavigationFlag) : null;
	} catch (error: unknown) {
		console.error("Failed to read admin navigation flag", error);
		return null;
	}
};

const clearAdminNavigationFlag = () => {
	sessionStorage.removeItem(ADMIN_NAV_FLAG_STORAGE_KEY);
};

function isValidTenantId(tenantId: string | undefined): tenantId is string {
	return Boolean(tenantId && tenantId !== "undefined" && tenantId !== "null");
}

function sanitizeTenantIds(tenantIds: Array<string | undefined>) {
	return tenantIds.filter(isValidTenantId);
}

export const Route = createFileRoute("/_app")({
	component: AppLayoutWrapper,
});

// Wrapper component to ensure authentication before rendering the app shell
function AppLayoutWrapper() {
	const { isSignedIn, isLoaded } = useAuth();
	const { user } = useUser();

	if (!isLoaded) {
		return <FullPageSpinner />;
	}

	if (!(isSignedIn && user)) {
		// Redirect to sign in if not authenticated
		return <RedirectToSignIn />;
	}

	return <RouteComponent />;
}

export function RouteComponent() {
	"use no memo";

	const defaultOpen = Cookies.get("sidebar:state") !== "false";

	// --- Tenant ID Check --- >
	const { tenantId } = useParams({ strict: false });
	const {
		data: organizationsData,
		isLoading: isOrganizationsLoading,
		isFetched: isOrganizationsFetched,
	} = useOrganizationsQuery({
		refetchOnWindowFocus: false,
		staleTime: 30_000,
	});
	const tenants = organizationsData?.organizations ?? EMPTY_ORGANIZATIONS;
	const hasFetchedTenants = isOrganizationsFetched && !isOrganizationsLoading;

	// Import auth state and organization context
	const { user } = useUser();
	const { organization: activeOrganization } = useOrganization();
	const { userMemberships, isLoaded: isOrgListLoaded } = useOrganizationList({
		userMemberships: {
			infinite: true,
		},
	});
	const organizations = userMemberships?.data ?? EMPTY_ORGANIZATIONS;
	const isAdmin = Boolean(organizationsData?.isAdmin) || isSuperAdminUser(user);
	const storedAdminSelection = isAdmin ? readAdminOrgSelection() : null;
	const navigate = useNavigate();

	const logEvent = useCallback((message: string, data?: unknown) => {
		if (!ROUTING_DEBUG_ENABLED) {
			return;
		}

		const entry: RouteLogEntry = {
			timestamp: new Date().toISOString(),
			message,
			data,
		};
		console.log(message, data);
		appendRouteLog(entry);
	}, []);

	const redirectToTenant = useCallback(
		(targetTenantId: string) => {
			navigate({
				to: "/$tenantId",
				params: { tenantId: targetTenantId },
				replace: true,
			});
		},
		[navigate],
	);

	const handleAdminTenantRouting = useCallback(() => {
		if (!hasFetchedTenants) {
			logEvent("Waiting for organizations API before admin validation");
			return;
		}

		if (!isOrgListLoaded) {
			return;
		}

		const activeTenantId = activeOrganization?.id;
		const databaseTenantIds = hasFetchedTenants
			? sanitizeTenantIds(tenants.map((tenant) => tenant.id))
			: [];
		const membershipTenantIds = isOrgListLoaded
			? sanitizeTenantIds(
					organizations.map((membership) => membership.organization.id),
				)
			: [];

		const { shouldStayOnTenant, fallbackTenantId } =
			resolveAdminFallbackTenantId({
				tenantId,
				storedSelectionOrgId: storedAdminSelection?.organizationId ?? null,
				activeTenantId,
				databaseTenantIds,
				membershipTenantIds,
			});

		if (shouldStayOnTenant && isValidTenantId(tenantId)) {
			const currentTenant = tenants.find((tenant) => tenant.id === tenantId);
			writeAdminOrgSelection(
				createAdminOrgSelection(tenantId, currentTenant?.name),
			);
			logEvent("Admin staying on requested organization", tenantId);
			return;
		}

		if (!fallbackTenantId) {
			logEvent("Admin user has no organizations available yet");
			return;
		}

		logEvent("Admin redirecting to organization", fallbackTenantId);
		redirectToTenant(fallbackTenantId);
	}, [
		activeOrganization?.id,
		hasFetchedTenants,
		isOrgListLoaded,
		logEvent,
		organizations,
		redirectToTenant,
		storedAdminSelection?.organizationId,
		tenantId,
		tenants,
	]);

	const handleMemberTenantRouting = useCallback(() => {
		const activeTenantId =
			activeOrganization?.id ?? organizationsData?.activeOrganizationId ?? null;

		if (!(isOrgListLoaded || activeTenantId)) {
			return;
		}

		const membershipTenantIds = sanitizeTenantIds(
			organizations.map((membership) => membership.organization.id),
		);
		const knownTenantIds = new Set([
			...membershipTenantIds,
			...(activeTenantId ? [activeTenantId] : []),
		]);

		if (knownTenantIds.size === 0) {
			if (!hasFetchedTenants) {
				logEvent("Waiting for organizations API before onboarding fallback");
				return;
			}

			logEvent("Non-admin user has no organization memberships available");
			navigate({
				to: "/app/onboarding",
				replace: true,
			});
			return;
		}

		const fallbackTenantId = activeTenantId ?? membershipTenantIds[0];

		if (!(isValidTenantId(tenantId) && knownTenantIds.has(tenantId))) {
			logEvent("Redirecting member to first organization", fallbackTenantId);
			redirectToTenant(fallbackTenantId);
		}
	}, [
		activeOrganization?.id,
		hasFetchedTenants,
		organizationsData?.activeOrganizationId,
		isOrgListLoaded,
		logEvent,
		navigate,
		organizations,
		redirectToTenant,
		tenantId,
	]);

	const skipDueToAdminNavigationFlag = useCallback(() => {
		const navFlag = readAdminNavigationFlag();
		logEvent("Checking admin nav flag:", navFlag);
		if (!navFlag) {
			return false;
		}

		const withinWindow =
			navFlag.timestamp > Date.now() - RECENT_ADMIN_NAVIGATION_WINDOW_MS;
		const matchesTenant = navFlag.targetOrgId === tenantId;
		if (withinWindow && navFlag.isAdmin) {
			if (matchesTenant) {
				logEvent(
					"Admin navigation detected, skipping validation for:",
					tenantId,
				);
				clearAdminNavigationFlag();
			} else {
				logEvent("Waiting for admin navigation target to settle:", {
					currentTenantId: tenantId,
					targetTenantId: navFlag.targetOrgId,
				});
			}
			return true;
		}

		clearAdminNavigationFlag();
		return false;
	}, [logEvent, tenantId]);

	// Refs to track component state and prevent race conditions
	const navigationInProgressRef = useRef(false);
	const mountedRef = useRef(true);

	// Cleanup effect to track mounted state
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			navigationInProgressRef.current = false;
		};
	}, []);

	useEffect(() => {
		// Guard: Don't trigger if navigation already in progress
		if (navigationInProgressRef.current) {
			logEvent("Navigation already in progress, skipping validation");
			return;
		}

		logEvent("Tenant validation check:", {
			tenantId,
			isAdmin,
			isOrganizationsLoading,
			tenantsCount: tenants.length,
			organizationsCount: organizations.length,
			databaseTenants: tenants.map((tenant) => ({
				id: tenant.id,
				name: tenant.name,
			})),
		});

		if (skipDueToAdminNavigationFlag()) {
			return;
		}

		// Set flag before performing routing logic
		navigationInProgressRef.current = true;

		if (isAdmin) {
			handleAdminTenantRouting();
		} else {
			handleMemberTenantRouting();
		}

		// Only reset if component still mounted
		if (mountedRef.current) {
			navigationInProgressRef.current = false;
		}
	}, [
		handleAdminTenantRouting,
		handleMemberTenantRouting,
		isAdmin,
		isOrganizationsLoading,
		logEvent,
		organizations,
		skipDueToAdminNavigationFlag,
		tenantId,
		tenants,
	]);

	// < --- Tenant ID Check ---

	// --- Original Render Logic --- >
	// If tenantId is missing or invalid, the redirect above will happen.
	// If tenantId IS present, render the layout.
	// Show loading spinner while we're waiting for organizations to load or redirecting
	if (!isValidTenantId(tenantId)) {
		return <FullPageSpinner />;
	}

	return (
		<SignedIn>
			<SearchProvider>
				<SidebarProvider defaultOpen={defaultOpen}>
					<SkipToMain />
					<AppSidebar />
					<div
						className={cn(
							"ml-auto flex h-screen w-full max-w-full flex-col",
							"peer-data-[state=collapsed]:w-[calc(100%_-_var(--sidebar-width-icon)_-_theme(spacing.4))]",
							"peer-data-[state=expanded]:w-[calc(100%_-_var(--sidebar-width)_-_theme(spacing.4))]",
							"transition-[width] duration-200 ease-linear",
						)}
						id="content"
					>
						<main className="flex-1 overflow-y-auto">
							<div className="flex-1 space-y-4 p-4 pt-4 md:p-8">
								<Suspense fallback={<FullPageSpinner />}>
									<Outlet />
								</Suspense>
							</div>
						</main>
					</div>
				</SidebarProvider>
			</SearchProvider>
		</SignedIn>
	);
}
